import React from 'react';

import { Box, Grid, Typography } from '@mui/material';

import { Field, Form } from '@hrlogics/react-components/form';
import { validateWithYupSchema } from '@hrlogics/react-form-utils/yup';

import { PermissiblePurposeStatus } from 'src/api';
import { Button } from 'src/components/common';
import { SelectField } from 'src/components/finalForm';
import { useCrediblePermissiblePurposeReferenceOptions } from 'src/hooks/permissiblePurposeReference';
import { usePermissiblePurposeCreate } from 'src/hooks/permissiblePurposes';
import { useCurrentUserVerifier } from 'src/hooks/verifier';

import { PermissiblePurposeCreateModalFields, PermissiblePurposeCreateModalValues } from './types';
import { permissiblePurposeCreateModalFormSchema } from './validation';

interface PermissiblePurposeCreateModalFormProps {
  closeModal: () => void;
  switchClosability: (isClosureAllowed: boolean) => void;
}

export const PermissiblePurposeCreateModalForm: React.FC<
  PermissiblePurposeCreateModalFormProps
> = ({ closeModal, switchClosability }) => {
  const { verifier } = useCurrentUserVerifier();
  const { createPermissiblePurpose } = usePermissiblePurposeCreate();

  const { crediblePermissiblePurposeOptions, isCrediblePermissiblePurposesLoading } =
    useCrediblePermissiblePurposeReferenceOptions(verifier);

  const onSubmit = React.useCallback(
    async (formValues: PermissiblePurposeCreateModalValues) => {
      switchClosability(false);
      try {
        await createPermissiblePurpose({
          variables: {
            data: {
              status: PermissiblePurposeStatus.Pending,
              verifier: {
                connect: { id: verifier?.id },
              },
              permissiblePurposeReference: {
                connect: {
                  id: formValues.permissiblePurposeReferenceId,
                },
              },
            },
          },
        });
        switchClosability(true);
        closeModal();
      } catch (error) {
        console.error(error);
        switchClosability(true);
      }
    },
    [closeModal, createPermissiblePurpose, switchClosability, verifier?.id],
  );

  return (
    <Box>
      <Typography variant="body1" color="text.secondary">
        Choose a Permissible Purpose you want to be credentialed
      </Typography>
      <Form<PermissiblePurposeCreateModalValues>
        onSubmit={onSubmit}
        subscription={{ valid: true, submitting: true }}
        containerProps={{ mt: '16px' }}
        initialValues={{
          permissiblePurposeReferenceId: crediblePermissiblePurposeOptions[0]?.value,
        }}
        validate={validateWithYupSchema(permissiblePurposeCreateModalFormSchema)}
      >
        {({ submitting, valid }) => {
          return (
            <React.Fragment>
              <Grid size={12}>
                <Field
                  name={PermissiblePurposeCreateModalFields.permissiblePurposeReferenceId}
                  component={SelectField}
                  options={crediblePermissiblePurposeOptions}
                  scrollableContent
                  fullWidth
                  required
                  disabled={isCrediblePermissiblePurposesLoading}
                />
              </Grid>
              <Box display="flex" gap={2} mt={4} justifyContent="center">
                <Button
                  variant="outlined"
                  color="infoButton"
                  onClick={() => closeModal()}
                  disabled={submitting}
                >
                  Cancel
                </Button>

                <Button
                  type="submit"
                  variant="contained"
                  disabled={!valid || submitting}
                  loading={submitting}
                >
                  Submit
                </Button>
              </Box>
            </React.Fragment>
          );
        }}
      </Form>
    </Box>
  );
};
